import type { ComputedRef, InjectionKey } from "vue";

export type MovableBlockId = "pma" | "navPage" | "lifetime";
export type LayoutZone = "above" | "below";

export interface MovableBlockState {
  id: MovableBlockId;
  zone: LayoutZone;
  order: number;
}

export interface DashboardMoveControlsApi {
  canMoveUp: ComputedRef<boolean> | boolean;
  canMoveDown: ComputedRef<boolean> | boolean;
  moveUp: () => void;
  moveDown: () => void;
}

export const DASHBOARD_MOVE_CONTROLS_KEY: InjectionKey<DashboardMoveControlsApi> =
  Symbol("dashboardMoveControls");

export const MOVABLE_BLOCK_LABELS: Record<MovableBlockId, string> = {
  pma: "PMA Reminder",
  navPage: "Navigation & page",
  lifetime: "Lifetime Income",
};

const DEFAULT_LAYOUT: MovableBlockState[] = [
  { id: "pma", zone: "above", order: 0 },
  { id: "navPage", zone: "below", order: 0 },
  { id: "lifetime", zone: "below", order: 1 },
];

const STORAGE_PREFIX = "dashboardMovableLayout_v1";

function storageKey(pt: string): string {
  return `${STORAGE_PREFIX}_${pt || "default"}`;
}

function normalizeLayout(blocks: MovableBlockState[]): MovableBlockState[] {
  const byId = new Map(blocks.map((block) => [block.id, block]));
  const next: MovableBlockState[] = DEFAULT_LAYOUT.map((fallback) => {
    const existing = byId.get(fallback.id);
    if (!existing) {
      return { ...fallback };
    }
    return {
      id: fallback.id,
      zone: existing.zone === "above" || existing.zone === "below" ? existing.zone : fallback.zone,
      order: Number.isFinite(existing.order) ? Number(existing.order) : fallback.order,
    };
  });
  return reindexZone(reindexZone(next, "above"), "below");
}

function reindexZone(blocks: MovableBlockState[], zone: LayoutZone): MovableBlockState[] {
  const zoneBlocks = blocks
    .filter((block) => block.zone === zone)
    .sort((a, b) => a.order - b.order);
  zoneBlocks.forEach((block, index) => {
    block.order = index;
  });
  return blocks;
}

export function loadDashboardLayout(pt: string): MovableBlockState[] {
  try {
    const raw = localStorage.getItem(storageKey(pt));
    if (!raw) {
      return DEFAULT_LAYOUT.map((block) => ({ ...block }));
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return DEFAULT_LAYOUT.map((block) => ({ ...block }));
    }
    return normalizeLayout(parsed as MovableBlockState[]);
  } catch (error) {
    console.error("Failed to load dashboard layout:", error);
    return DEFAULT_LAYOUT.map((block) => ({ ...block }));
  }
}

export function saveDashboardLayout(pt: string, blocks: MovableBlockState[]): void {
  try {
    localStorage.setItem(storageKey(pt), JSON.stringify(normalizeLayout(blocks)));
  } catch (error) {
    console.error("Failed to save dashboard layout:", error);
  }
}

export function getBlocksInZone(
  blocks: MovableBlockState[],
  zone: LayoutZone
): MovableBlockState[] {
  return blocks
    .filter((block) => block.zone === zone)
    .sort((a, b) => a.order - b.order);
}

function cloneBlocks(blocks: MovableBlockState[]): MovableBlockState[] {
  return blocks.map((block) => ({ ...block }));
}

export function moveBlockUp(
  blocks: MovableBlockState[],
  id: MovableBlockId
): MovableBlockState[] {
  const next = cloneBlocks(blocks);
  const current = next.find((block) => block.id === id);
  if (!current) {
    return next;
  }

  const zoneBlocks = getBlocksInZone(next, current.zone);
  const index = zoneBlocks.findIndex((block) => block.id === id);

  if (index > 0) {
    const prev = zoneBlocks[index - 1];
    const currentOrder = current.order;
    current.order = prev.order;
    prev.order = currentOrder;
    return normalizeLayout(next);
  }

  if (current.zone === "below") {
    current.zone = "above";
    current.order = getBlocksInZone(next, "above").length;
    return normalizeLayout(next);
  }

  return next;
}

export function moveBlockDown(
  blocks: MovableBlockState[],
  id: MovableBlockId
): MovableBlockState[] {
  const next = cloneBlocks(blocks);
  const current = next.find((block) => block.id === id);
  if (!current) {
    return next;
  }

  const zoneBlocks = getBlocksInZone(next, current.zone);
  const index = zoneBlocks.findIndex((block) => block.id === id);

  if (index > -1 && index < zoneBlocks.length - 1) {
    const after = zoneBlocks[index + 1];
    const currentOrder = current.order;
    current.order = after.order;
    after.order = currentOrder;
    return normalizeLayout(next);
  }

  if (current.zone === "above") {
    current.zone = "below";
    current.order = -1;
    return normalizeLayout(next);
  }

  return next;
}

export function canMoveBlockUp(
  blocks: MovableBlockState[],
  id: MovableBlockId
): boolean {
  const current = blocks.find((block) => block.id === id);
  if (!current) {
    return false;
  }
  if (current.zone === "below") {
    return true;
  }
  const zoneBlocks = getBlocksInZone(blocks, "above");
  return zoneBlocks.findIndex((block) => block.id === id) > 0;
}

export function canMoveBlockDown(
  blocks: MovableBlockState[],
  id: MovableBlockId
): boolean {
  const current = blocks.find((block) => block.id === id);
  if (!current) {
    return false;
  }
  if (current.zone === "above") {
    return true;
  }
  const zoneBlocks = getBlocksInZone(blocks, "below");
  const index = zoneBlocks.findIndex((block) => block.id === id);
  return index > -1 && index < zoneBlocks.length - 1;
}
